/**
 * Finds the index of an object in an array based on the `name` property.
 *
 * @param {Array<Permission>} array - The array of objects to search.
 * @param {string} name - The name to find in the array.
 * @returns {number} - The index of the object if found, otherwise `-1`.
 */
const getNodeName = (item) =>
    item?.name || item?.permissionName || item?.permission_name || item?.label || '';

const normalizePermissionLabel = (value) =>
    String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

const normalizePermissionKey = (value) =>
    normalizePermissionLabel(value).replace(/,/g, '');

const indexFinder = (array, name) => {
    const target = normalizePermissionLabel(name);
    return array.findIndex(
        (item) => normalizePermissionLabel(getNodeName(item)) === target
    );
};
const findPermissionNode = (userPerms, path = []) => {
    let current = userPerms;
    let node = null;

    for (const level of path) {
        const permissionIndex = indexFinder(current, level);
        if (permissionIndex === -1) {
            return null;
        }
        node = current[permissionIndex];
        current = node.children || [];
    }

    return node;
};

const collectPermissionNames = (node) => {
    const names = new Set();
    if (!node) return names;

    const walk = (item) => {
        if (!item) return;
        const label = getNodeName(item);
        if (label) names.add(label);
        (item.children || []).forEach(walk);
    };

    walk(node);
    return names;
};

/**
 * Collects all permission names granted under one or more module paths.
 */
export const getModulePermissionNames = (userPerms, modulePaths = []) => {
    const names = new Set();

    modulePaths.forEach((path) => {
        const node = findPermissionNode(userPerms, path);
        collectPermissionNames(node).forEach((name) => names.add(name));
    });

    return names;
};

const collectAllMatchingTierNames = (nodes, knownTierNames, names) => {
    if (!nodes?.length) return;

    nodes.forEach((item) => {
        const label = getNodeName(item);
        const normalizedLabel = normalizePermissionLabel(label);
        const normalizedKey = normalizePermissionKey(label);

        if (
            label &&
            (knownTierNames.has(normalizedLabel) || knownTierNames.has(normalizedKey))
        ) {
            names.add(label);
        }
        collectAllMatchingTierNames(item.children, knownTierNames, names);
    });
};

/**
 * Returns the highest approval amount limit from granted tier permissions.
 */
export const getHighestGrantedApprovalLimit = (grantedNames, ranges = []) => {
    const normalizedGranted = new Set(
        [...grantedNames].map((name) => normalizePermissionLabel(name))
    );
    const normalizedGrantedKeys = new Set(
        [...grantedNames].map((name) => normalizePermissionKey(name))
    );

    let highestMax = 0;

    ranges.forEach(({ max, perm }) => {
        const matchesTier =
            normalizedGranted.has(normalizePermissionLabel(perm)) ||
            normalizedGrantedKeys.has(normalizePermissionKey(perm));

        if (matchesTier) {
            highestMax = Math.max(highestMax, max);
        }
    });

    return highestMax;
};

export const canApproveAmountByTierPermissions = (
    userPerms,
    modulePaths,
    ranges,
    billAmount
) => {
    const amount = Number(String(billAmount ?? '').replace(/,/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) return false;

    const knownTierNames = new Set(
        ranges.flatMap(({ perm }) => [
            normalizePermissionLabel(perm),
            normalizePermissionKey(perm),
        ])
    );

    const grantedNames = new Set(getModulePermissionNames(userPerms, modulePaths));
    collectAllMatchingTierNames(userPerms, knownTierNames, grantedNames);

    const highestLimit = getHighestGrantedApprovalLimit(grantedNames, ranges);

    return amount <= highestLimit;
};

export const getServiceModulePaths = (serviceName) => [
    ['General Services', serviceName],
    [serviceName],
    ['Commercial Operations', serviceName],
    ['Billing and Collection', serviceName],
];

const findPermissionInTree = (nodes, targetName) => {
    const target = normalizePermissionLabel(targetName);
    if (!nodes?.length || !target) return false;

    for (const node of nodes) {
        if (normalizePermissionLabel(getNodeName(node)) === target) {
            return true;
        }
        if (findPermissionInTree(node.children, targetName)) {
            return true;
        }
    }
    return false;
};

/**
 * Checks if a service/module permission is granted anywhere in the user's tree.
 * Tries known module paths first, then falls back to a full-tree name search.
 */
export const hasServicePermission = (userPerms, serviceName) => {
    if (!serviceName || !userPerms?.length) return false;

    const hasPathMatch = getServiceModulePaths(serviceName).some((path) =>
        CheckPermissions(userPerms, ...path)
    );
    if (hasPathMatch) return true;

    return findPermissionInTree(userPerms, serviceName);
};

/**
 * Finds a permission node by name anywhere in the tree.
 */
const findPermissionNodeByName = (nodes, targetName) => {
    const target = normalizePermissionLabel(targetName);
    if (!nodes?.length || !target) return null;

    for (const node of nodes) {
        if (normalizePermissionLabel(getNodeName(node)) === target) {
            return node;
        }
        const found = findPermissionNodeByName(node.children, targetName);
        if (found) return found;
    }
    return null;
};

/**
 * Checks Basic Permissions → Create (or other levels) under a service,
 * regardless of whether the service sits under Billing and Collection,
 * General Services, Commercial Operations, or is nested elsewhere.
 */
export const hasServiceLevelPermission = (userPerms, serviceName, ...levels) => {
    if (!serviceName || !userPerms?.length) return false;

    if (
        hasWorkflowPermissionForModule(
            userPerms,
            getServiceModulePaths(serviceName),
            ...levels
        )
    ) {
        return true;
    }

    const serviceNode = findPermissionNodeByName(userPerms, serviceName);
    if (!serviceNode) return false;

    if (!levels.length) return true;

    if (CheckPermissions(serviceNode.children || [], ...levels)) {
        return true;
    }

    // Fallback when children nesting broke (e.g. idt/parent_idt type mismatch):
    // walk all nodes via parent_idt and confirm the leaf sits under this service.
    const allNodes = [];
    const collect = (nodes) => {
        (nodes || []).forEach((node) => {
            allNodes.push(node);
            collect(node.children);
        });
    };
    collect(userPerms);

    const byId = new Map();
    allNodes.forEach((node) => {
        if (node?.idt === undefined || node?.idt === null) return;
        byId.set(String(node.idt), node);
    });

    const leafName = levels[levels.length - 1];
    const leafTarget = normalizePermissionLabel(leafName);
    const serviceId = String(serviceNode.idt);
    const serviceLabel = normalizePermissionLabel(serviceName);

    return allNodes.some((leaf) => {
        if (normalizePermissionLabel(getNodeName(leaf)) !== leafTarget) {
            return false;
        }

        let current = leaf;
        const seen = new Set();
        while (
            current &&
            current.parent_idt !== null &&
            current.parent_idt !== undefined &&
            current.parent_idt !== ''
        ) {
            const parentKey = String(current.parent_idt);
            if (seen.has(parentKey)) break;
            seen.add(parentKey);

            if (parentKey === serviceId) return true;

            const parent = byId.get(parentKey);
            if (!parent) break;
            if (normalizePermissionLabel(getNodeName(parent)) === serviceLabel) {
                return true;
            }
            current = parent;
        }

        return false;
    });
};

export const hasWorkflowPermissionForModule = (
    userPerms,
    modulePaths,
    ...workflowLevels
) => {
    const pathsToTry = Array.isArray(modulePaths?.[0]) ? modulePaths : [modulePaths];

    for (const basePath of pathsToTry) {
        if (CheckPermissions(userPerms, ...basePath, ...workflowLevels)) {
            return true;
        }
    }

    const leafPermission = workflowLevels[workflowLevels.length - 1];
    const grantedNames = getModulePermissionNames(userPerms, pathsToTry);
    const target = normalizePermissionLabel(leafPermission);

    return [...grantedNames].some(
        (name) => normalizePermissionLabel(name) === target
    );
};

/**
 * @typedef {Object} Permission
 * @property {string} name - The name of the permission.
 * @property {Permission[]} [children] - Optional nested permissions.
 */

/**
 * Checks whether a user has the required permissions by navigating through a hierarchical permission structure.
 *
 * @param {Permission[]} userPerms - The user's permission hierarchy.
 * @param {...string} permissionLevels - The required permission levels to check, in a nested sequence.
 * @returns {boolean} - `true` if the user has all the required permissions, otherwise `false`.
 *
 * @example
 * const userPermissions = [
 *   { name: "Access Management", children: [{ name: "Role Management", children: [{ name: "Create" }] }] },
 *   { name: "General Services", children: [{ name: "New Connection" }] }
 * ];
 *
 * CheckPermissions(userPermissions, "Access Management", "Role Management", "Create"); // Returns true
 * CheckPermissions(userPermissions, "General Services", "New Connection", "Create"); // Returns false
 */
const CheckPermissions = (userPerms, ...permissionLevels) => {
    let currentPermissions = userPerms;

    for (let level of permissionLevels) {
        // Find the permission at the current level
        let permissionIndex = indexFinder(currentPermissions, level);
        if (permissionIndex === -1) {
            return false; // Permission not found
        }
        // Move to the next level's children if they exist
        currentPermissions = currentPermissions[permissionIndex].children || [];
    }

    return true; // All permissions found
}

export default CheckPermissions;